import { z } from 'zod';
import { request, useMutation, useQueryClient } from '@applyboard/ui-utils';
import { VasErrorBody } from '../common/errors/VasError';

export const CREATE_REWARD_SIGNUP_V1_URL = '/api/rewards_signup/v1';

export const CreateRewardSignUpV1RequestSchema = z.object({
  rp_id: z.string().trim(),
  source: z.string().trim(),
});

export type CreateRewardSignUpV1Request = z.infer<typeof CreateRewardSignUpV1RequestSchema>;

export const CreateRewardSignUpV1ResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.string(),
    attributes: z.object({
      rp_id: z.string(),
      source: z.string(),
      type: z.string().optional(),
      created_at: z.string().optional(),
      updated_at: z.string().optional(),
    }),
  }),
});

export type CreateRewardSignUpV1Response = z.infer<typeof CreateRewardSignUpV1ResponseSchema>;

export class RewardsError extends Error {
  public readonly errorBody: VasErrorBody;

  constructor(body: VasErrorBody, message: string) {
    super(message);
    this.errorBody = body;
  }
}

export function useRewardSignUpV1() {
  const queryClient = useQueryClient();
  const { mutate, isLoading, isError } = useMutation(
    async (inputBody: CreateRewardSignUpV1Request) => {
      const parsedRequest: CreateRewardSignUpV1Request =
        CreateRewardSignUpV1RequestSchema.parse(inputBody);

      return request(CREATE_REWARD_SIGNUP_V1_URL, {
        body: parsedRequest,
        method: 'POST',
        schema: CreateRewardSignUpV1ResponseSchema,
        throwError: async (response: Response) => {
          const jsonMessage = await response.json();
          throw new RewardsError(jsonMessage, 'Reward Sign Up V1 Failed');
        },
      });
    },
  );

  const rewardSignUp = (
    params: CreateRewardSignUpV1Request,
    onSuccess?: (result: CreateRewardSignUpV1Response) => void,
    onError?: (error: unknown) => void,
  ) => {
    mutate(params, {
      onSuccess: async (response: CreateRewardSignUpV1Response) => {
        queryClient.invalidateQueries([`${CREATE_REWARD_SIGNUP_V1_URL}_${response?.data?.id}`]);
        onSuccess?.(response);
      },
      onError: (error: unknown) => onError?.(error),
    });
  };

  return {
    rewardSignUp,
    isProcessingRewardSignUp: isLoading,
    rewardSignUpIsError: isError,
  };
}
